import { NAME, version } from '../common/constants';
import ErrorCode from '../common/error-code';
import RtcError from '../common/rtc-error';
import innerEmitter from '../inner-emitter/inner-emitter';
import { INNER_EVENT } from '../inner-emitter/inner-type';
import loggerManager from '../utils/log/logger-manager';
import { getUint32Version, isNumber, isUndefined } from '../utils/utils';

const SUCCESS_KEY = 1;
const FAILED_KEY = 0;
/**
 * Key Value 上报模块
 * @link 一级 Key
 *  - 国内（事件上报）：https://zhiyan.woa.com/monitor/13524/detail_chart/#/chart/detail_chart/vertical?query=true&env=prod&calculation=&currentRange=eyJ0eXBlIjoibGF0ZXN0Iiwib2Zmc2V0Ijo2MDQ4MDB9&compareType=time&compareTime=JTdCJTIydmFsdWUlMjIlM0EtODY0MDAwMDAlMkMlMjJsYWJlbCUyMiUzQSUyMiVFNSU5MCU4QyVFNiVBRiU5NCglRTQlQjglODAlRTUlQTQlQTklRTUlODklOEQpJTIyJTdE&interval=60&conds=%7B%22top_lvl_app_name%22%3A%22trtc_sdk_monitor%22,%22sec_lvl_app_name%22%3A%22default%22,%22metrics%22%3A%5B%22state_cnt%22%5D,%22tags%22%3A%7B%22terminal_type%22%3A%7B%22op%22%3A%22equal%22,%22values%22%3A%5B%22webrtc%22%5D,%22gather%22%3Afalse%7D%7D%7D
 *  - 海外：https://zhiyan.sg.woa.com/monitor/1000160/detail_chart/#/chart/detail_chart/vertical?query=true&env=prod&calculation=&currentRange=eyJ0eXBlIjoibGF0ZXN0Iiwib2Zmc2V0Ijo2MDQ4MDB9&compareType=time&compareTime=JTdCJTIydmFsdWUlMjIlM0EtODY0MDAwMDAlMkMlMjJsYWJlbCUyMiUzQSUyMiVFNSU5MCU4QyVFNiVBRiU5NCglRTQlQjglODAlRTUlQTQlQTklRTUlODklOEQpJTIyJTdE&interval=60&conds=%7B%22top_lvl_app_name%22%3A%22trtc_sdk_monitor%22,%22sec_lvl_app_name%22%3A%22default%22,%22metrics%22%3A%5B%22state_cnt%22,%22dis_cnt%22%5D,%22tags%22%3A%7B%7D%7D
 * @link 二级 Key
 *  - 国内：
 *    - 按时间维度（分布上报）：https://zhiyan.woa.com/monitor/13524/detail_chart/#/chart/detail_chart/vertical?query=true&env=prod&type=minute&currentRange=eyJ0eXBlIjoibGF0ZXN0Iiwib2Zmc2V0Ijo2MDQ4MDB9&compareType=time&compareTime=JTdCJTIydmFsdWUlMjIlM0EtODY0MDAwMDAlMkMlMjJsYWJlbCUyMiUzQSUyMiVFNyU4RSVBRiVFNiVBRiU5NCglRTUlODklOEQlRTQlQjglODAlRTUlQTQlQTkpJTIyJTdE&gap=1&conds=%7B%22app_id%22%3A11672,%22metrices%22%3A%5B92710%5D,%22tags%22%3A%7B%22terminal_type%22%3A%7B%22op%22%3A%22equal%22,%22values%22%3A%5B%22webrtc%22%5D,%22gather%22%3Afalse%7D%7D%7D
 *    - 饼图：https://zhiyan.woa.com/monitor/13524/dashboard/#/dashboard?env=prod&mode=tab&type=public&node=169&board=170
 *  - 海外：https://zhiyan.sg.woa.com/monitor/1000160/dashboard/#/dashboard?env=prod&mode=tree&type=public&node=495&board=517&code=TOF4TeyJrZXkiOiJTOUp5TlBWUEdOZEJocmlXZ21Db1liakZFQXFDcHRUZiIsImlzcyI6IjEwLjk5LjIwOC4zOCIsImhrIjoiIiwiYXVkIjoibmduIiwiaGFzaCI6IkQ3RjJENkE5QzUxQ0IxNjA5QjZFOEY3Rjg4QUQ2RTZFOTkwNjY0Q0U3RDUwMUM3MTUxNkQ1Q0ZEMEU1NzYyRjYiLCJuaCI6IjU5RjlBRTRGMUY2RTA4NTRDOTlGNUY0QjEwOUM1NDdDREVCN0EzNTZDMjY4RUVGRTE4Mjg1OEY4NEFDM0U2MTgifQ
 * @link Key 区间划分 https://doc.weixin.qq.com/sheet/e3_AEMAlgbdAFwgVBkHZmGSB0w82pvxD?scode=AJEAIQdfAAoZTrjM9kAEMAlgbdAFw&tab=000002
 * @author rychouwei
 */
class KVStatManager {
  countMap: Map<KV_REPORT_KEY, number> = new Map();
  distributionMap: Map<KV_REPORT_KEY, Map<number, number>> = new Map();
  version?: string;
  log = loggerManager.createLogger({ id: 'kv' });

  constructor(inner = true) {
    if (inner) {
      innerEmitter.on(INNER_EVENT.LOCAL_TRACK_CAPTURE_SUCCESS, ({ track, cost }) => {
        this.addSuccessEvent({ key: track.kind === NAME.AUDIO ? KV_REPORT_KEY_AUDIO_CAPTURE.START_MICROPHONE : KV_REPORT_KEY_VIDEO_CAPTURE.START_CAMERA, cost });
      });
      innerEmitter.on(INNER_EVENT.LOCAL_TRACK_CAPTURE_FAILED, ({ track, error }) => {
        this.addFailedEvent({ key: track.kind === NAME.AUDIO ? KV_REPORT_KEY_AUDIO_CAPTURE.START_MICROPHONE : KV_REPORT_KEY_VIDEO_CAPTURE.START_CAMERA, error });
      });
      innerEmitter.on(INNER_EVENT.SSO_SWITCH, ({ enable }) => {
        this.log.info(`${enable ? 'enable' : 'disable'} sso`);
        enable ? this.addSuccessEvent({ key: KV_REPORT_KEY_SSO.SSO_SWITCH }) : this.addFailedEvent({ key: KV_REPORT_KEY_SSO.SSO_SWITCH });
      });
    }
  }

  getReportData(userSig?: string, reportToken?: string) {
    const data = {
      msg_sdk_basic_info: {
        uint32_sdk_version: getUint32Version(this.version || version),
        uint32_terminal_type: 15,
        bytes_device_name: '',
        bytes_os_version: '',
        uint32_framework: 30,
        uint32_network_type: 0
      },
      // 跟后台联调时，可以传入 userInfo 用于定位
      // msg_user_info: {
      //   str_identifier: 'qer',
      //   uint64_tinyid: '1234567890'
      // },
      stats_count: [...this.countMap.entries()].map(([key, count]) => ({ uint32_key: key, uint32_count: count })),
      stats_distribution: [...this.distributionMap.entries()]
        .map(([key, distributionItem]) => ({
          uint32_key: key,
          distribution_items: [...distributionItem.entries()].map(([itemKey, value]) => ({ uint32_item_key: itemKey, uint32_item_value: value }))
        })),
      str_user_sig: userSig,
      bytes_report_token: reportToken
    };
    this.countMap.clear();
    this.distributionMap.clear();
    return data;
  }

  clear() {
    this.countMap.clear();
    this.distributionMap.clear();
  }

  isEnumKey(key: number) {
    const last3Number = +String(key).slice(-3);
    return last3Number >= 700 && last3Number < 799;
  }

  isErrorCodeKey(key: number) {
    const last3Number = +String(key).slice(-3);
    return last3Number >= 600 && last3Number < 699;
  }

  isCountKey(key: number) {
    const last3Number = +String(key).slice(-3);
    return last3Number >= 0 && last3Number < 599;
  }

  isNumberKey(key: number) {
    const last3Number = +String(key).slice(-3);
    return last3Number >= 800 && last3Number < 899;
  }

  addCount<T extends number>({ key, useUV = false }: { key: KV_REPORT_KEY | T, useUV?: boolean; }) {
    if (!this.isCountKey(key)) {
      this.log.debug(`${key} is not count key, last 3 number should be 0~599`);
      return;
    }
    if (useUV && this.countMap.has(key)) return;
    this.countMap.set(key, (this.countMap.get(key) || 0) + 1);
  }

  addEnum<T extends number>({ key, value, useUV = true }: { key: KV_REPORT_KEY | T, value: number, useUV?: boolean; }) {
    if (!this.isEnumKey(key)) {
      return this.log.debug(`${key} is not enum key, last 3 number should be 700~799`);
    }
    if (useUV && this.countMap.has(key)) return;
    this.countMap.set(key, (this.countMap.get(key) || 0) + 1);
    const map = this.distributionMap?.get(key) || new Map();
    map.set(value, (map.get(value) || 0) + 1);
    this.distributionMap.set(key, map);
  }

  /**
   * 按区间分布，上报数字。可用于上报耗时
   * @param key
   * @param {number} value 数值大小
   * @param {number|number[]} [split = 100] 拆分粒度，默认 100
   * @param {number} [max = 5000] value 最大值，默认值 5000，防止数值过大，后台落库太多的 key。用于耗时上报时，则默认耗时最大 5000ms，超过这个数值一般没有统计意义了。
   * 例如：进房耗时 532ms，则按默认 100ms 粒度拆分，最终上报的子 key 是 5，子 value 是 1。代表进房耗时在 500-600ms 区间，上报次数 1.
   * 若传入数组，数组需按从小到大排序，例如，耗时是 200ms，按这个数组拆分 [0, 50, 100, 300, 500, 1000]，最终上报的子 key 是 3，因为 200ms 在 index 3-4 之间，取 3.
   * @returns
   */
  addNumber<T extends number>({ key, value, split = 100, useUV = false, max = 5000 }: { key: KV_REPORT_KEY | T, value: number, split?: number | number[], useUV?: boolean; max?: number }) {
    if (!this.isNumberKey(key)) {
      return this.log.debug(`${key} is not number key, last 3 number should be 800~899`);
    }
    if (useUV && this.countMap.has(key)) return;
    if (value > max) value = max;
    this.countMap.set(key, (this.countMap.get(key) || 0) + 1);
    const map = this.distributionMap?.get(key) || new Map();
    let SPLIT_INDEX = 0;
    if (isNumber(split)) {
      SPLIT_INDEX = Math.floor(value / split);
    } else {
      for (let index = split.length - 1; index > 0; index--) {
        if (value > split[index]) {
          SPLIT_INDEX = index;
          break;
        }
      }
    }
    map.set(SPLIT_INDEX, (map.get(SPLIT_INDEX) || 0) + 1);
    this.distributionMap.set(key, map);
  }

  /**
   * 统计成功率，成功时打点
   * @param {object} param
   * @param {number} param.key 末三位需在 [700, 799] 区间
   * @param {number} [param.cost] 耗时，单位 ms
   * @param {number} [param.timeKey] 统计耗时的 key，末三位需在 [800, 899] 区间，默认 key + 100。
   * @returns
   */
  addSuccessEvent<T extends number>({ key, cost, timeKey, split }: { key: KV_REPORT_KEY | T, timeKey?: KV_REPORT_KEY, cost?: number, split?: number | number[]; }) {
    if (!key) return;
    // 成功率统计基于 enum 实现，使用 PV 而不是 UV
    this.addEnum({ key, value: SUCCESS_KEY, useUV: false });

    if (cost) {
      const last3Number = +String(key).slice(-3);
      if (last3Number < 800 && last3Number >= 700) {
        this.addNumber({ key: timeKey || key + 100, value: cost, split });
      } else if (!timeKey) {
        this.log.debug(`time stat ignored, ${key}`);
      }
    }
  }

  /**
   * 统计成功率，失败时打点
   * @param {object} param
   * @param {number} param.key 末三位需在 [700, 799] 区间
   * @param {number|RtcError|Error} [param.error]  错误码、错误对象
   * @returns
   */
  addFailedEvent<T extends number>({ key, error }: { key: KV_REPORT_KEY | T, error?: RtcError | number | Error | MediaError | null; }) {
    if (!key) return;
    let errorCode = ErrorCode.UNKNOWN;
    if (error) {
      if (isNumber(error)) errorCode = error;
      // @ts-ignore
      else if (!isUndefined(error.extraCode) || !isUndefined(error.code)) errorCode = error.extraCode || error.code;
    }
    // 成功率统计基于 enum 实现，使用 PV 而不是 UV
    this.addEnum({ key, value: FAILED_KEY, useUV: false });
    this.addEnum({ key, value: Math.abs(errorCode), useUV: false });
  }
}

export type KV_REPORT_KEY = KV_REPORT_KEY_API | KV_REPORT_KEY_AUDIO_CAPTURE | KV_REPORT_KEY_AUDIO_MANAGER | KV_REPORT_KEY_VIDEO_CAPTURE | KV_REPORT_KEY_NETWORK | KV_REPORT_KEY_VIDEO_MANAGER | KV_REPORT_KEY_START_PLUGIN | KV_REPORT_KEY_UPDATE_PLUGIN | KV_REPORT_KEY_STOP_PLUGIN | KV_REPORT_KEY_DOWNLOAD | KV_REPORT_KEY_AUDIO_PLAYER | KV_REPORT_KEY_VIDEO_PLAYER | KV_REPORT_KEY_DEVICEDETECTOR_PLUGIN | KV_REPORT_KEY_VIDEO_ENCODE | KV_REPORT_KEY_VIDEO_DECODE | KV_REPORT_KEY_AUDIO_ENCODE | KV_REPORT_KEY_AUDIO_DECODE | KV_REPORT_KEY_DEBUG_PLUGIN | KV_REPORT_KEY_DETECTOR | KV_REPORT_KEY_AUDIO_PROCESSOR_PLUGIN | KV_REPORT_KEY_SSO | KV_REPORT_KEY_AUDIO_CONTEXT | KV_REPORT_KEY_WHIP;

// 500
export enum KV_REPORT_KEY_API {
  // enum success rate
  enterRoom = 500700,
  exitRoom = 500701,
  switchRole = 500702,
  destroy = 500703,
  startLocalAudio = 500704,
  updateLocalAudio = 500705,
  stopLocalAudio = 500706,
  startLocalVideo = 500707,
  updateLocalVideo = 500708,
  stopLocalVideo = 500709,
  startScreenShare = 500710,
  updateScreenShare = 500711,
  stopScreenShare = 500712,
  startRemoteVideo = 500713,
  updateRemoteVideo = 500714,
  stopRemoteVideo = 500715,
  muteRemoteAudio = 500716,
  setRemoteAudioVolume = 500717,
  use = 500718,
  switchRoom = 500719,
  getPermissions = 500720,

  // count
  sendSEIMessage = 500000,
  sendCustomMessage = 500001
}

export enum KV_REPORT_KEY_START_PLUGIN {
  AudioMixer = 550700,
  AIDenoiser = 551700,
  VirtualBackground = 570700,
  Beauty = 571700,
  Watermark = 572700,
  BasicBeauty = 574700,
  FaceDetector = 575700,
  CDNStreaming = 590700,
  DeviceDetector = 591700,
  Debug = 592700,
  SmallStreamAutoSwitcher = 593700,
  VideoMixer = 594700,
  AudioProcessor = 595700,
  LEBPlayer = 596700,
  RealtimeTranscriber = 597700
}
export enum KV_REPORT_KEY_UPDATE_PLUGIN {
  AudioMixer = 550701,
  AIDenoiser = 551701,
  VirtualBackground = 570701,
  Beauty = 571701,
  Watermark = 572701,
  BasicBeauty = 574701,
  FaceDetector = 575701,
  CDNStreaming = 590701,
  DeviceDetector = 591701,
  Debug = 592701,
  SmallStreamAutoSwitcher = 593701,
  VideoMixer = 594701,
  AudioProcessor = 595701,
  LEBPlayer = 596701,
  RealtimeTranscriber = 597701
}

export enum KV_REPORT_KEY_STOP_PLUGIN {
  AudioMixer = 550702,
  AIDenoiser = 551702,
  VirtualBackground = 570702,
  Beauty = 571702,
  Watermark = 572702,
  BasicBeauty = 574702,
  FaceDetector = 575702,
  CDNStreaming = 590702,
  DeviceDetector = 591702,
  Debug = 592702,
  SmallStreamAutoSwitcher = 593702,
  VideoMixer = 594702,
  AudioProcessor = 595702,
  LEBPlayer = 596702,
  RealtimeTranscriber = 597702
}

export enum KV_REPORT_KEY_FACE_FOCUS {
  DualStageCropping = 570704,
  MovingPortrait = 570705,
}
// 591
export enum KV_REPORT_KEY_DEVICEDETECTOR_PLUGIN {
  FirstPageSuccess = 591703,
  FirstPageFail = 591704,
  cameraSuccess = 591705,
  cameraFail = 591706,
  MicSuccess = 591707,
  MIcFail = 591708,
  speakerSuccess = 591709,
  speakerFail = 591710,
  networkSuccess = 591711,
  networkFail = 591712,
  clickSelect = 591713
}
// 592
export enum KV_REPORT_KEY_DEBUG_PLUGIN {
  EXPORT_LOG = 592703,
  UPLOAD_LOG = 592704,
  LOAD_SCRIPT = 592705,
}

// 595
export enum KV_REPORT_KEY_AUDIO_PROCESSOR_PLUGIN {
}

export enum KV_REPORT_KEY_SMALL_STREAM_AUTO_SWITCHER {
  SWITCH_TO_SMALL = 593000,
  SWITCH_TO_BIG = 593001
}

// 501
export const enum KV_REPORT_KEY_AUDIO_CAPTURE {
  START_MICROPHONE = 501700,
  MICROPHONE_CAHNNELS = 501701,
  MICROPHONE_SAMPLERATE = 501702
}

// 502
export const enum KV_REPORT_KEY_AUDIO_MANAGER {
  MIX = 502700,
  SET_NODE = 502701,
  DELETE_NODE = 502702,
  USE_LOOP = 502001,
  USE_VOLUME = 502002,
  USE_PLAYBACK_RATE = 502003,
  USE_OPERATION = 502004,
  USE_SEEK = 502005,
  USE_DURATION_CHANGE = 502006,
  USE_CURRENT_TIME = 502007,
  USE_ENDED = 502008,
  USE_TRACK = 502009,
}

// 503
export const enum KV_REPORT_KEY_AUDIO_ENCODE {
  ENCODE_RESULT = 503700,
  ENCODE_FAILED_OS = 503701
}
// 504
export const enum KV_REPORT_KEY_AUDIO_DECODE {
  DECODE_RESULT = 504700,
  DECODE_FAILED_OS = 504701,
  DOWNGRADE_RESULT = 504702,
  DOWNGRADE_WEBCODECS = 504703,
  DOWNGRADE_WASM = 504704,
  DOWNGRADE_HEALTH = 504705
}

// 506
export const enum KV_REPORT_KEY_AUDIO_PLAYER {
  MEDIA_ERROR_RETRY = 506700,
  MEDIA_ERROR_RETRY_WEBCODEC = 506701
}

// 507
export const enum KV_REPORT_KEY_AUDIO_CONTEXT {
  SUSPENDED_DURATION = 507800
}

// 508
export const enum KV_REPORT_KEY_WHIP {
  WHIP_RESULT = 508700, // 0: whip success, 1: whip fail but join success 2: whip fail and join fail
  WHIP_REBUILD_RESULT = 508701 // 0: rebuild success 1: rebuild fail
}

// 516
export const enum KV_REPORT_KEY_VIDEO_PLAYER {
  MEDIA_ERROR_RETRY = 516700,
  RENDER_FIRST_FRAME_TIME = 516820
}

export const enum KV_REPORT_VALUE_AUDIO_MANAGER_MIX {
  MICROPHONE = 0,
  DENOISER = 1,
  GAIN = 2,
  MUSIC = 3
}
// 511
export const enum KV_REPORT_KEY_VIDEO_CAPTURE {
  START_CAMERA = 511700
}
// 512
export const enum KV_REPORT_KEY_VIDEO_MANAGER {
  START_VIDEO_CONTEXT_GL = 512700,
  START_VIDEO_CONTEXT_2D = 512701,
  VIDEO_CONTEXT_GL_RESULT = 512702,
  VIDEO_CONTEXT_2D_RESULT = 512703,
}

// 513
export const enum KV_REPORT_KEY_VIDEO_ENCODE {
  ENCODER_TYPE = 513700,
  ENCDER_HW_SW = 513701,
  ENCODE_RESULT = 513702,
  ENCODE_FAILED_OS = 513703,
  ENCODE_H264_RESULT = 513704,
  ENCODE_H265_RESULT = 513705,
  ENCODE_VP8_RESULT = 513706,
  ENCODE_CAPABILITIES = 513707,
  ENCODE_H265_FALLBACK = 513000,
  H264_PROFILE_LEVEL_ID_HIGH = 513708,
  H264_PROFILE_LEVEL_ID_MAIN = 513709
}

// 514
export const enum KV_REPORT_KEY_VIDEO_DECODE {
  DECODER_TYPE = 514700,
  DECODER_HW_SW = 514701,
  DECODE_RESULT = 514702,
  DECODE_FAILED_OS = 514703,
  DOWNGRADE_RESULT = 514704,
  DOWNGRADE_WEBCODECS_VIDEO = 514705,
  DOWNGRADE_WEBCODECS_2D = 514706,
  DOWNGRADE_WASM_WEGBL = 514707,
  DOWNGRADE_WASM_VIDEO = 514708,
  DOWNGRADE_WASM_2D = 514709,
  DECODE_H264_RESULT = 514710,
  DECODE_H265_RESULT = 514711,
  DECODE_VP8_RESULT = 514712,

  DECODE_CAPABILITIES = 514713,
  H264_PROFILE_LEVEL_ID_HIGH = 514714,
  H264_PROFILE_LEVEL_ID_MAIN = 514715,
  RENDER_FREEZE_RATE = 514850, // 渲染卡顿时间 / 总时间
  DATA_FREEZE_RATE = 514851, // 数据卡顿时间 / 总时间
  VIDEO_CONSUME_RENDER_RATE = 514852, // 已解码的帧数量 / 接收的帧数量
}

// 515
export const enum KV_REPORT_KEY_VIDEO_POST_PROCESS {
  ALPHA_RESULT = 515700
}

// 521
export const enum KV_REPORT_KEY_NETWORK {
  SCHEDULE = 521700,
  SIGNAL_CHANNEL_SETTUP = 521701,
  WS_RECONNECT = 521702,
  SPC_CONNECT = 521703,
  SPC_RECONNECT = 521704,
  MPC_CONNECT = 521705,
  MPC_RECONNECT = 521706,
  SPC_INITIALIZED = 521707,
  JOIN_SEND_CMD = 521708,
  SWITCH_RELAY = 521709,
  SWITCH_RELAY_FALLBACK = 521710,
  PUBLISH_STATE_NOT_MATCHED = 521000,
  ICE_CONNECT = 521711,
  SET_LOCAL_DESCRIPTION = 521712,
  SET_REMOTE_DESCRIPTION = 521713,
  PUBLISH = 521714,
  UNPUBLISH = 521715,
  SUBSCRIBE = 521716,
  UNSUBSCRIBE = 521717,
  PING = 521718,
  PUBLISH_FAILED_DURING_CALL = 521719,
  WS_CONNECT = 521720,
  // WHIP
  WHIP_REQUEST = 521730,
  WHIP_PC_CONNECT = 521731,
  WHIP_WS_REBUILD = 521732,
  WHIP_TOTAL = 521733
}


export const enum KV_STAT_TYPE {
  COUNT = 'count',
  ENUM = 'enum',
  NUMBER = 'number',
}

export const enum KV_REPORT_KEY_DOWNLOAD {
  DOWNLOAD = 522700,
  LOAD_WASM = 522701,
  LOAD_SCRIPT = 522702,
}

export const enum KV_REPORT_KEY_DETECTOR {
  CHECK_SYSTEM_REQUIREMENT_COST = 523800
}

export const enum KV_REPORT_KEY_SMALL_MODE {
  SMALL_MODE_CANVAS = 524700, // success(1) 标识总启动次数，fail(0) 标识总失败次数
  SMALL_MODE_API = 524701,
  SWITCH_DOWNLINK_SMALL = 524702, // 检测到下行黑屏，切换到大流，1 代表切换到大流
}

// 小流模式错误码常量
export const enum SMALL_MODE_ERROR_CODE {
  INIT_FAILED = 10001, // 小流模式初始化失败
  BLACK_SCREEN_FAILED = 10002, // 小流模式中途黑屏失败
}

// 自动切换策略评价，切换之后的帧率是切换前的多少倍
export const enum KV_REPORT_KEY_SMALL_STREAM_AUTO_SWITCHER_FRAME_RATE_RATIO {
  AFTER_SWITCH_TO_SMALL = 593800,
}

export const enum KV_REPORT_KEY_SSO {
  SSO_SWITCH = 525701, // 统计触发兜底逻辑的比例
}

// 526 PlaybackQualitySwitcher
export const enum KV_REPORT_KEY_PLAYBACK_QUALITY_SWITCHER {
  // enum(success/fail): 手动下切成功率
  MANUAL_DOWNGRADE_RESULT = 526700,
  // enum(success/fail): 手动上切成功率
  MANUAL_UPGRADE_RESULT = 526701,
  // enum(success/fail): 自动下切成功率
  DOWNGRADE_RESULT = 526702,
  // enum(success/fail): 自动上切成功率
  UPGRADE_RESULT = 526703,
  // enum(success/fail): 解码失败后恢复成功率
  DECODE_FALLBACK_RESULT = 526704,
  // number: 切换后 fps 改善率 (百分比，切换后6s平均fps / 触发切换fps * 100)
  FPS_IMPROVEMENT_RATIO = 526800,
}

const kvStatManager = new KVStatManager(true);

// 给 Room Call 使用
const outterKVStatManager = new KVStatManager(false);

export { outterKVStatManager, kvStatManager, KVStatManager };
export default kvStatManager;
